import React, { useEffect, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { errorDetail } from "../../helper/errorDetail";
import { LOW_CREDIT_THRESHOLD } from "./trialConstants";
import UpgradeCta from "./UpgradeCta";

const DAY_MS = 24 * 60 * 60 * 1000;

const roleName = (profile) => (
  typeof profile?.role === "object" ? profile.role.role_name : null
);

const hasTrialHistory = (profile) => (
  Boolean(profile?.trial_started_at) && profile?.trial_converted_at == null
);

const hasSubscribedAllowance = (balance) => (
  Number.isFinite(balance?.monthly_credit_allowance)
  && balance.monthly_credit_allowance > 0
);

export const shouldShowFreemiumTrial = (profile, balance) => {
  if (!profile || profile.is_verified === false || profile.trial_converted_at) return false;
  if (hasSubscribedAllowance(balance)) return false;
  return roleName(profile) === "freemium" || hasTrialHistory(profile);
};

const refusalNotice = (detail, fallback) => {
  const code = detail?.error_code;
  const message = errorDetail(detail, fallback);
  if (code === "SUBSCRIPTION_REQUIRED") return `Subscription required. ${message}`;
  if (code === "TRIAL_EXPIRED") return `Trial expired. ${message}`;
  return message;
};

export default function FreemiumTrialIndicator({
  profile,
  balance,
  balanceUnavailable = false,
  refusalDetail,
  hideActiveStatus = false,
  now,
}) {
  const [currentTime, setCurrentTime] = useState(() => now ?? Date.now());

  useEffect(() => {
    if (Number.isFinite(now)) {
      setCurrentTime(now);
      return undefined;
    }

    const expiresAt = Date.parse(profile?.trial_expires_at);
    if (!Number.isFinite(expiresAt) || expiresAt <= currentTime) return undefined;

    const remaining = expiresAt - currentTime;
    const displayedDays = Math.ceil(remaining / DAY_MS);
    const nextBoundary = remaining - ((displayedDays - 1) * DAY_MS);
    const timer = setTimeout(() => setCurrentTime(Date.now()), Math.max(1, nextBoundary));
    return () => clearTimeout(timer);
  }, [currentTime, now, profile?.trial_expires_at]);

  if (!shouldShowFreemiumTrial(profile, balance)) return null;

  const expiresAt = Date.parse(profile?.trial_expires_at);
  const expired = Number.isFinite(expiresAt) && expiresAt <= currentTime;
  const credits = Number.isFinite(balance?.balance)
    ? balance.balance
    : Number.isFinite(balance?.total)
      ? balance.total
      : null;
  const unavailable = balanceUnavailable || credits === null;
  const state = expired
    ? "expired"
    : credits === 0
      ? "empty"
      : credits !== null && credits <= LOW_CREDIT_THRESHOLD
        ? "low"
        : "active";
  const days = !expired && Number.isFinite(expiresAt)
    ? Math.max(1, Math.ceil((expiresAt - currentTime) / DAY_MS))
    : null;
  const refusalCode = refusalDetail?.error_code;
  const unknownSubscription = balance == null;
  const neutralExpiry = expired && unknownSubscription && !refusalCode;
  const subscriptionRequired = (expired && !unknownSubscription)
    || refusalCode === "TRIAL_EXPIRED"
    || refusalCode === "SUBSCRIPTION_REQUIRED";
  const showCta = neutralExpiry || state === "empty" || subscriptionRequired || Boolean(refusalCode);

  let notice = null;
  if (refusalCode) {
    notice = refusalNotice(refusalDetail, "Access to this feature is unavailable.");
  } else if (state === "expired") {
    notice = neutralExpiry
      ? "Your freemium trial has ended. Credit status is unavailable."
      : "Your freemium trial has expired. Upgrade to continue using metered features.";
  } else if (state === "empty") {
    notice = refusalDetail
      ? refusalNotice(refusalDetail, "No trial credits remain.")
      : "No trial credits remain. No declined request details are available yet.";
  } else if (state === "low") {
    notice = "Your trial credits are running low.";
  }

  if (hideActiveStatus && state === "active" && !notice) return null;

  return (
    <Box
      data-testid="trial-indicator"
      data-trial-state={state}
      sx={{
        position: "fixed",
        zIndex: 2400,
        top: { xs: 64, md: 64 },
        right: { xs: 8, md: 16 },
        width: { xs: "calc(100% - 16px)", md: "auto" },
        maxWidth: 520,
      }}
    >
      <Alert severity={state === "active" ? "info" : "warning"}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
          <Typography component="span" variant="body2">
            {unavailable ? (
              <span data-testid="trial-indicator-unavailable">Credits unavailable</span>
            ) : (
              <>
                <span data-testid="trial-credits">{credits}</span>
                {profile.trial_credit_allowance != null ? (
                  <> / <span data-testid="trial-allowance">{profile.trial_credit_allowance}</span> credits</>
                ) : " credits"}
              </>
            )}
            {days !== null ? <> · <span data-testid="trial-days">{days}</span> {days === 1 ? "day" : "days"} remaining</> : null}
          </Typography>
          {notice ? <Typography data-testid="trial-notice" variant="body2">{notice}</Typography> : null}
          {showCta ? (
            <UpgradeCta
              detail={refusalDetail}
              destination={subscriptionRequired ? "subscription" : "credits"}
              label={neutralExpiry ? "View usage" : subscriptionRequired ? "Upgrade to continue" : "Add credits"}
            />
          ) : null}
        </Stack>
      </Alert>
    </Box>
  );
}
